import React from 'react';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {Button} from '@ui/buttons/button';
import {Link} from 'react-router-dom';
import {Trans} from '@ui/i18n/trans';
import {Track} from '@app/web-player/tracks/track';
import {TrackImage} from '@app/web-player/tracks/track-image/track-image';
import {TrackLink} from '@app/web-player/tracks/track-link';
import {ArtistLinks} from '@app/web-player/artists/artist-links';
import {MusicNoteIcon} from '@ui/icons/material/MusicNote';
import type {PlayerLimitType} from '@common/player/limits/native-limit-reached';

interface PlayerControlLockedDialogProps {
  action?: string;
  reason?: string;
  retryAfterSeconds?: number | null;
  track?: Track;
  overlayZIndex?: number;
  limitType?: PlayerLimitType;
  titleKey?: string;
  messageKey?: string;
  actionKey?: string;
}

export function PlayerControlLockedDialog({
  action,
  reason,
  retryAfterSeconds,
  track,
  limitType,
  titleKey,
  messageKey,
  actionKey,
}: PlayerControlLockedDialogProps) {
  const {close} = useDialogContext();
  const actionLabel = getActionLabel(action);
  const titleMessage = titleKey || getDialogTitle(limitType, reason);
  const bodyMessage = messageKey || getDialogMessage(limitType, reason);
  const buttonMessage = actionKey || 'Upgrade now';

  return (
    <Dialog size="xl" radius="rounded-xl">
      <DialogHeader>
        <Trans message={titleMessage} />
      </DialogHeader>
      <DialogBody>
        <div className="flex w-full flex-col items-center gap-6 md:flex-row md:items-start">
          <div className="mb-20 w-full flex-[0.8] md:mb-0 md:w-auto">
            <div className="flex flex-col items-center justify-center text-center">
              {track ? (
                <TrackImage
                  track={track}
                  size="mb-20 h-180 w-180"
                  className="rounded"
                />
              ) : (
                <span className="mb-20 flex h-180 w-180 items-center justify-center rounded bg-fg-base/4">
                  <MusicNoteIcon className="text-divider" size="text-6xl" />
                </span>
              )}
              {track ? (
                <div className="w-full min-w-0 max-w-full text-center">
                  <TrackLink
                    track={track}
                    className="block min-w-0 max-w-full truncate break-all text-sm"
                  />
                  {track.artists?.length ? (
                    <div className="text-xs text-muted">
                      <ArtistLinks artists={track.artists} className="mx-auto max-w-full md:mx-0" />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="w-full min-w-0 flex-1">
            <div className="mb-30 text-center md:text-left">
              <h1 className="mb-10 break-words text-xl font-extrabold leading-tight md:text-2xl">
                <Trans message={titleMessage} />
              </h1>
              {messageKey || limitType ? (
                <p className="break-words text-muted">
                  <Trans message={bodyMessage} />
                </p>
              ) : (
                reason === 'overQuota' ? (
                  <p className="break-words text-muted">
                    <Trans
                      message="You've reached your current limit for :action. Upgrade to continue without friction."
                      values={{action: actionLabel}}
                    />
                  </p>
                ) : (
                  <p className="break-words text-muted">
                    <Trans
                      message="Your current plan does not include :action. Upgrade to unlock this control."
                      values={{action: actionLabel}}
                    />
                  </p>
                )
              )}
              {reason === 'overQuota' && retryAfterSeconds ? (
                <p className="mt-10 break-words text-xs text-muted">
                  <Trans
                    message="You can retry in about :minutes min."
                    values={{minutes: Math.max(1, Math.ceil(retryAfterSeconds / 60))}}
                  />
                </p>
              ) : null}
            </div>
            <div className="px-24 py-20">
              <div className="flex flex-col items-center gap-8 md:flex-row md:justify-center">
                <Button
                  className="order-2 min-w-160 md:order-1"
                  variant="text"
                  onClick={() => close()}
                >
                  <Trans message="Maybe later" />
                </Button>
                <Button
                  className="order-1 min-w-160 md:order-2"
                  autoFocus
                  variant="flat"
                  color="primary"
                  elementType={Link}
                  to="/pricing"
                  onClick={() => close()}
                >
                  <Trans message={buttonMessage} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

function getActionLabel(action?: string): string {
  switch (action) {
    case 'play':
      return 'Play';
    case 'next':
      return 'Next';
    case 'prev':
      return 'Previous';
    case 'seek':
      return 'Seek';
    case 'shuffle':
      return 'Shuffle';
    case 'repeat':
      return 'Repeat';
    case 'queue_auto_fill':
      return 'Queue Auto Fill';
    case 'queue_auto_mix':
      return 'Queue Auto Mix';
    default:
      return 'this action';
  }
}

function getDialogTitle(limitType?: PlayerLimitType, reason?: string): string {
  switch (limitType) {
    case 'preview':
      return 'Listen to full tracks';
    case 'listen24h':
      return 'Keep listening without interruption';
    case 'nextPerHour':
      return 'Skip forward without limits';
    case 'prevPerHour':
      return 'Go back without limits';
    default:
      return reason === 'overQuota'
        ? 'Need more control while listening?'
        : 'Unlock Full Player Control';
  }
}

function getDialogMessage(limitType?: PlayerLimitType, reason?: string): string {
  switch (limitType) {
    case 'preview':
      return "You've reached the preview limit for this track. Upgrade to listen in full.";
    case 'listen24h':
      return "You've reached your listening limit for the last 24 hours. Upgrade to keep playing.";
    case 'nextPerHour':
      return "You've reached your skip-forward limit for this hour. Upgrade to keep controlling playback.";
    case 'prevPerHour':
      return "You've reached your replay limit for this hour. Upgrade to keep controlling playback.";
    default:
      return reason === 'overQuota'
        ? "You've reached your current limit. Upgrade to continue without friction."
        : 'Your current plan does not include this control. Upgrade to unlock it.';
  }
}
